import React, { useEffect, useRef, useState } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import './OutsourcingPrivateProjects.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const emptyForm = (employeeId = '', date = today()) => ({ employee_id: String(employeeId), date, status: 'present', check_in: '', check_out: '', notes: '' });
const attendanceForm = row => ({ employee_id: String(row.employee_id), date: row.date?.slice(0, 10) || today(), status: row.status, check_in: row.check_in?.slice(0, 5) || '', check_out: row.check_out?.slice(0, 5) || '', notes: row.notes || '' });
const money = value => value === null || value === undefined ? 'Not configured' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value));
const termsForm = member => ({ employee_id: member.id, pay_basis: member.terms?.pay_basis || 'monthly', pay_rate: member.terms?.pay_rate ?? '', billing_basis: member.terms?.billing_basis || 'monthly', billing_rate: member.terms?.billing_rate ?? '', transport_per_day: member.terms?.transport_per_day ?? 0, insurance_amount: member.terms?.insurance_amount ?? 0 });

export default function OutsourcingPrivateProjects() {
  let user = {};
  try { user = JSON.parse(localStorage.getItem('user') || '{}'); } catch { /* Deny access without a valid session. */ }
  const companyId = localStorage.getItem('selectedCompanyId');
  if (user?.role !== 'super_admin') {
    return <main className="private-projects"><h1>Restricted access</h1><p>Only superadmins can access private outsourcing projects.</p></main>;
  }
  return <PrivateProjects key={companyId} companyId={companyId} />;
}

function PrivateProjects({ companyId }) {
  const base = `${API}/companies/${companyId}/outsourcing/private-projects`;
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [month, setMonth] = useState(() => today().slice(0, 7));
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);
  const [tab, setTab] = useState('team');
  const [form, setForm] = useState(() => emptyForm());
  const [saving, setSaving] = useState(false);
  const [editingTerms, setEditingTerms] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const saveController = useRef(null);

  useEffect(() => () => saveController.current?.abort(), []);

  const readResponse = async response => {
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        setProjects([]);
        setDetails(null);
        setForm(emptyForm());
        setDenied(true);
      }
      const validation = body.errors ? Object.values(body.errors).flat().join(' ') : '';
      throw new Error(validation || body.message || 'The request could not be completed. Please try again.');
    }
    return body;
  };

  useEffect(() => {
    const controller = new AbortController();
    fetch(base, { headers: getAuthHeaders(), cache: 'no-store', signal: controller.signal })
      .then(readResponse)
      .then(body => {
        if (controller.signal.aborted) return;
        const list = body.data || [];
        setProjects(list);
        setSelectedId(list[0]?.id || '');
      })
      .catch(err => { if (!controller.signal.aborted) setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
    // Requests belong to the company workspace and never persist project data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  useEffect(() => {
    if (!selectedId) return;
    const controller = new AbortController();
    setDetails(null);
    setError('');
    setSaveMessage('');
    setEditingTerms(null);
    setDetailLoading(true);
    fetch(`${base}/${selectedId}?month=${encodeURIComponent(month)}`, { headers: getAuthHeaders(), cache: 'no-store', signal: controller.signal })
      .then(readResponse)
      .then(body => {
        if (controller.signal.aborted) return;
        setDetails(body);
        setForm(emptyForm(body.members?.[0]?.id || '', month === today().slice(0, 7) ? today() : `${month}-01`));
      })
      .catch(err => { if (!controller.signal.aborted) setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setDetailLoading(false); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, selectedId, month]);

  const members = details?.members || [];
  const attendance = details?.attendance || [];
  const lastDay = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
  const currentRecord = attendance.find(row => String(row.employee_id) === String(form.employee_id) && row.date?.slice(0, 10) === form.date);
  const changeSelection = (employeeId, date) => {
    const record = attendance.find(row => String(row.employee_id) === String(employeeId) && row.date?.slice(0, 10) === date);
    setForm(record ? attendanceForm(record) : emptyForm(employeeId, date));
    setSaveMessage('');
  };

  const saveAttendance = async event => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    setSaveMessage('');
    const controller = new AbortController();
    saveController.current = controller;
    let saved = false;
    try {
      const response = await fetch(`${base}/${selectedId}/attendance`, {
        method: 'PUT', headers: getAuthHeaders({ 'Content-Type': 'application/json' }), cache: 'no-store', signal: controller.signal,
        body: JSON.stringify({ ...form, employee_id: Number(form.employee_id), check_in: form.check_in || null, check_out: form.check_out || null, notes: form.notes.trim() || null }),
      });
      const body = await readResponse(response);
      if (controller.signal.aborted) return;
      const record = body.data || body;
      setDetails(previous => ({ ...previous, attendance: [record, ...(previous?.attendance || []).filter(row => !(String(row.employee_id) === String(record.employee_id) && row.date?.slice(0, 10) === record.date?.slice(0, 10)))] }));
      setSaveMessage('Attendance saved.');
      saved = true;
      const refreshed = await fetch(`${base}/${selectedId}?month=${encodeURIComponent(month)}`, { headers: getAuthHeaders(), cache: 'no-store', signal: controller.signal }).then(readResponse);
      if (!controller.signal.aborted) setDetails(refreshed);
    } catch (err) {
      if (!controller.signal.aborted) setError(saved ? `Attendance was saved, but the project totals could not be refreshed. ${err.message}` : err.message);
    } finally {
      if (!controller.signal.aborted) setSaving(false);
    }
  };

  const saveTerms = async event => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    setSaveMessage('');
    const controller = new AbortController();
    saveController.current = controller;
    try {
      const response = await fetch(`${base}/${selectedId}/terms`, {
        method: 'PUT', headers: getAuthHeaders({ 'Content-Type': 'application/json' }), cache: 'no-store', signal: controller.signal,
        body: JSON.stringify({ ...editingTerms, month, pay_rate: editingTerms.pay_rate === '' ? null : Number(editingTerms.pay_rate), billing_rate: editingTerms.billing_rate === '' ? null : Number(editingTerms.billing_rate), transport_per_day: Number(editingTerms.transport_per_day), insurance_amount: Number(editingTerms.insurance_amount) }),
      });
      const body = await readResponse(response);
      if (controller.signal.aborted) return;
      setDetails(body);
      setSaveMessage('Pay settings saved for this month.');
    } catch (err) {
      if (!controller.signal.aborted) setError(err.message);
    } finally {
      if (!controller.signal.aborted) setSaving(false);
    }
  };

  return (
    <main className="private-projects">
      <header className="private-projects-heading">
        <div><p className="private-projects-eyebrow">Outsourcing Management</p><h1>Private projects</h1></div>
        <span className="private-projects-access">Superadmins only</span>
      </header>
      <p className="private-projects-intro">Project records and employee attendance are kept separate from other dashboards.</p>
      {error && <div role="alert" className="private-projects-error">{error}</div>}
      {denied ? <p>Access is restricted. Sign in with a superadmin account.</p> : <>
        {loading && <p role="status">Loading private projects…</p>}
        {!loading && projects.length === 0 && !error && <div className="private-projects-panel">No private projects in this company.</div>}
        {projects.length > 0 && <div className="private-projects-list" aria-label="Private projects">
          {projects.map(project => <button type="button" key={project.id} disabled={saving} aria-pressed={String(project.id) === String(selectedId)} onClick={() => setSelectedId(project.id)} className={`private-projects-card${String(project.id) === String(selectedId) ? ' selected' : ''}`}>
            <strong>{project.name}</strong><span>Customer: {project.customer_name}</span><span>{project.members_count || 0} employees · {project.status}</span>
          </button>)}
        </div>}
        {detailLoading && <p role="status">Loading project details…</p>}
        {details && <section className="private-projects-panel" aria-label={`${details.project.name} details`}>
          <div className="private-projects-project-heading"><div><h2>{details.project.name}</h2><p>Customer: {details.project.customer_name} <span className="private-projects-status">{details.project.status}</span></p></div><label className="private-projects-month">Month<input type="month" required value={month} disabled={saving} onChange={e => { if (/^\d{4}-\d{2}$/.test(e.target.value)) setMonth(e.target.value); }} /></label></div>
          {details.summary && <>
            <div className="private-projects-summary">
              {[['Total project cost', details.summary.total_cost], ['Customer charges', details.summary.customer_charge], ['Difference', details.summary.net]].map(([label, amount]) => <div key={label}><span>{label}</span><strong>{money(amount)}</strong></div>)}
            </div>
            {!details.summary.complete && <p className="private-projects-calculation-note">{details.summary.configured_members || 0} of {details.summary.total_members || members.length} employees configured. Complete pay and billing rates, and any missing attendance times, to calculate the full totals.</p>}
            <p className="private-projects-calculation-note">USD · Calculations apply only to this project and month. They do not create payroll or accounting entries.</p>
          </>}
          <div className="private-projects-tabs" role="tablist" aria-label="Project details">
            <button type="button" id="private-team-tab" role="tab" aria-selected={tab === 'team'} aria-controls="private-team" onClick={() => setTab('team')}>Team & costs ({members.length})</button>
            <button type="button" id="private-attendance-tab" role="tab" aria-selected={tab === 'attendance'} aria-controls="private-attendance" onClick={() => setTab('attendance')}>Attendance</button>
          </div>
          {tab === 'team' && <div id="private-team" role="tabpanel" aria-labelledby="private-team-tab">
            {editingTerms && <form className="private-projects-form" onSubmit={saveTerms}>
              <h3>Pay settings · {members.find(member => member.id === editingTerms.employee_id)?.name} · {month}</h3>
              <p className="private-projects-calculation-note">Monthly rates are fixed for this month. Daily rates use present days; hourly rates use recorded check-in and check-out times. Leave a rate blank until it is agreed.</p>
              <fieldset disabled={saving} className="private-projects-terms-fields">
                <label>Employee pay basis<select value={editingTerms.pay_basis} onChange={e => setEditingTerms({ ...editingTerms, pay_basis: e.target.value })}><option value="monthly">Monthly</option><option value="daily">Daily</option><option value="hourly">Hourly</option></select></label>
                <label>Employee pay rate (USD)<input type="number" min="0" step="0.01" value={editingTerms.pay_rate} onChange={e => setEditingTerms({ ...editingTerms, pay_rate: e.target.value })} placeholder="Not set" /></label>
                <label>Customer billing basis<select value={editingTerms.billing_basis} onChange={e => setEditingTerms({ ...editingTerms, billing_basis: e.target.value })}><option value="monthly">Monthly</option><option value="daily">Daily</option><option value="hourly">Hourly</option></select></label>
                <label>Customer billing rate (USD)<input type="number" min="0" step="0.01" value={editingTerms.billing_rate} onChange={e => setEditingTerms({ ...editingTerms, billing_rate: e.target.value })} placeholder="Not set" /></label>
                <label>Transport per present day (USD)<input type="number" min="0" step="0.01" required value={editingTerms.transport_per_day} onChange={e => setEditingTerms({ ...editingTerms, transport_per_day: e.target.value })} /></label>
                <label>Monthly insurance (USD)<input type="number" min="0" step="0.01" required value={editingTerms.insurance_amount} onChange={e => setEditingTerms({ ...editingTerms, insurance_amount: e.target.value })} /></label>
              </fieldset>
              <div className="private-projects-form-footer"><span role="status">{saveMessage}</span><div><button type="button" disabled={saving} className="private-projects-cancel" onClick={() => { setEditingTerms(null); setSaveMessage(''); }}>Close</button><button type="submit" disabled={saving} className="private-projects-save">{saving ? 'Saving…' : 'Save pay settings'}</button></div></div>
            </form>}
            <div className="private-projects-table-wrap"><table><thead><tr><th>Employee</th><th>Present days</th><th>Hours</th><th>Salary</th><th>Transport</th><th>Insurance</th><th>Total cost</th><th>Customer charge</th><th>Difference</th><th>Actions</th></tr></thead><tbody>
              {members.map(member => <tr key={member.id}><td>{member.name}<small className="private-projects-employee-code">{member.employee_code || '—'}</small></td><td>{member.calculation?.present_days ?? 0}</td><td>{member.calculation?.recorded_hours ?? 0}{member.calculation?.hours_complete === false && <small className="private-projects-missing">Missing times</small>}</td>{['salary', 'transport', 'insurance', 'total_cost', 'customer_charge', 'net'].map(field => <td key={field}>{money(member.calculation?.[field])}</td>)}<td><button type="button" disabled={saving} onClick={() => { setEditingTerms(termsForm(member)); setSaveMessage(''); }}>Edit pay settings</button></td></tr>)}
              {!members.length && <tr><td colSpan="10">No employees assigned.</td></tr>}
            </tbody></table></div>
          </div>}
          {tab === 'attendance' && <div id="private-attendance" role="tabpanel" aria-labelledby="private-attendance-tab">
            {members.length > 0 && <form className="private-projects-form" onSubmit={saveAttendance}>
              <h3>{currentRecord ? 'Edit attendance' : 'Record attendance'}</h3>
              <fieldset disabled={saving}>
                <label>Employee<select required value={form.employee_id} onChange={e => changeSelection(e.target.value, form.date)}>{members.map(member => <option key={member.id} value={member.id}>{member.name}{member.employee_code ? ` · ${member.employee_code}` : ''}</option>)}</select></label>
                <label>Date<input type="date" required min={`${month}-01`} max={`${month}-${lastDay}`} value={form.date} onChange={e => changeSelection(form.employee_id, e.target.value)} /></label>
                <label>Status<select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}><option value="present">Present</option><option value="absent">Absent</option><option value="leave">Leave</option></select></label>
                <label>Check in<input type="time" value={form.check_in} onChange={e => setForm({ ...form, check_in: e.target.value })} /></label>
                <label>Check out<input type="time" value={form.check_out} onChange={e => setForm({ ...form, check_out: e.target.value })} /></label>
                <label className="private-projects-notes">Notes<textarea rows="2" maxLength="2000" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></label>
              </fieldset>
              <div className="private-projects-form-footer"><span role="status">{saveMessage}</span><button type="submit" className="private-projects-save" disabled={saving}>{saving ? 'Saving…' : 'Save attendance'}</button></div>
            </form>}
            <h3>Attendance records</h3>
            <div className="private-projects-table-wrap"><table><thead><tr><th>Date</th><th>Employee</th><th>Status</th><th>Check in</th><th>Check out</th><th>Notes</th><th><span className="sr-only">Actions</span></th></tr></thead><tbody>
              {[...attendance].sort((a, b) => String(b.date).localeCompare(String(a.date))).map(row => <tr key={row.id}><td>{row.date?.slice(0, 10)}</td><td>{members.find(member => String(member.id) === String(row.employee_id))?.name || 'Employee'}</td><td className="private-projects-capitalize">{row.status}</td><td>{row.check_in?.slice(0, 5) || '—'}</td><td>{row.check_out?.slice(0, 5) || '—'}</td><td className="private-projects-record-note">{row.notes || '—'}</td><td><button type="button" disabled={saving} onClick={() => { setForm(attendanceForm(row)); setSaveMessage(''); }}>Edit</button></td></tr>)}
              {!attendance.length && <tr><td colSpan="7">No attendance recorded for this project yet.</td></tr>}
            </tbody></table></div>
          </div>}
        </section>}
      </>}
    </main>
  );
}
